import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import type { ConsentHubResponse } from '@atlassian/browser-storage-controls/consent-hub-service/types';
// eslint-disable-next-line jira/import-whitelist
import {
	ATLASSIAN_CONSENT_COOKIE_KEY,
	ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY,
	ATLASSIAN_SHOW_BANNER_COOKIE_KEY,
} from '@atlassian/browser-storage-controls/constants';
import type { PrefetchedConsentPreferenceData } from '@atlassian/browser-storage-controls/controllers/banner-controls/types';
import { getAtlassianAccountId } from '@atlassian/jira-common-util-get-tenant-context';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isFetchError, isValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	useResource,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/import-whitelist
import { getCookie } from '@jira/platform__browser-storage-controls/src/utils/get-cookie/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import getPillarLocalDevelopment from '@jira/ssr__global-build-context/src/getPillarLocalDevelopment.tsx';
import { CONSENT_BANNER_USER_PREFERENCES_KEY } from './consentBannerUserPreferencesKey.constants.tsx';

export type JiraPrefetchedConsentPreferenceData = PrefetchedConsentPreferenceData & {
	shouldShowBanner?: boolean;
};

const ERROR_ID = 'consentBannerUserPreferencesResource';

/**
 * Errors this consent hub call is expected to produce in the wild: an HTTP error response
 * (401/403 for an unauthenticated or unauthorised session, 404, or a 5xx from the service) and
 * client/network level failures (timeout, abort, connection loss, unparseable body).
 *
 * None of them mean the page cannot be rendered. The resource is fetched on every route, so
 * letting them bubble fails the whole SSR render for a banner that has a safe fallback: with no
 * preference data the consumer shows the banner, which is the compliant default.
 *
 * Anything else is an unexpected (likely programming) error and is rethrown.
 */
const isRecoverableConsentHubError = (error: unknown): boolean =>
	isFetchError(error) ||
	isClientFetchError(error) ||
	(error instanceof Error && isValidationError(error));

const fetchConsentPreferences = async (): Promise<JiraPrefetchedConsentPreferenceData> => {
	/**
	 * On server we need to request the api using a server to server api (atl-preferences-center-elb)
	 * otherwise the request will not be authenticated correctly.
	 *
	 * However Pillar does not support ASAP, so we need to use the client side api call in local development.
	 *
	 * See https://atlassian.slack.com/archives/C017Y3EPAQ4/p1754626460768439?thread_ts=1754601759.549879&cid=C017Y3EPAQ4
	 */
	if (__SERVER__ && !getPillarLocalDevelopment()) {
		const userId = getAtlassianAccountId();
		if (userId) {
			return performGetRequest<ConsentHubResponse>(`/rest/user/${userId}/consents/cookies`, {
				credentials: 'include',
				headers: {
					'x-ssr-service-name': 'atl-preferences-center',
				},
			});
		}
	}
	return performGetRequest<ConsentHubResponse>(
		'/gateway/api/consenthub/session/user/consents/cookies',
	);
};

const fetchConsentPreferencesWithErrorHandling =
	async (): Promise<JiraPrefetchedConsentPreferenceData> => {
		try {
			return await fetchConsentPreferences();
		} catch (error: unknown) {
			if (!isRecoverableConsentHubError(error)) {
				throw error;
			}

			const normalisedError = error instanceof Error ? error : new Error(String(error));
			logger.safeErrorWithoutCustomerData(ERROR_ID, normalisedError.message);
			fireErrorAnalytics({
				meta: {
					id: ERROR_ID,
					packageName: 'jiraRouterResourcesCookieConsent',
					teamName: 'jfp-magma',
				},
				error: normalisedError,
				// Backend and connectivity failures here are monitored server side, and are handled
				// rather than fatal, so they should not raise Sentry issues against the renderer.
				skipSentry: true,
			});

			// Null equivalent response: no known preferences, so the consumer falls back to showing
			// the banner and the client refetches once hydrated.
			return {};
		}
	};

const consentBannerUserPreferencesResource = createResource<JiraPrefetchedConsentPreferenceData>({
	type: 'CONSENT_BANNER_USER_PREFERENCES_SSR',
	getKey: () => CONSENT_BANNER_USER_PREFERENCES_KEY,
	getData: async () => {
		const consentToken = getCookie(ATLASSIAN_CONSENT_COOKIE_KEY, true);
		const fallbackConsentToken = getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY, true);
		const shouldShowBannerBit = getCookie(ATLASSIAN_SHOW_BANNER_COOKIE_KEY, true);
		const shouldShowBanner =
			shouldShowBannerBit !== undefined && shouldShowBannerBit !== null
				? !!Number(shouldShowBannerBit)
				: undefined;
		// If we have cached cookie data, bundle it up, with a potential fallback token
		if (consentToken) {
			return { consentToken, fallbackConsentToken, shouldShowBanner };
		}
		if (!isFeatureEnabled('consent-platform', 'consenthub')) {
			return {};
		}
		if (fg('jiv-23790_add_error_handling_consent_bnr_prefs_res')) {
			return fetchConsentPreferencesWithErrorHandling();
		}
		return fetchConsentPreferences();
	},
	maxAge: 15 * 60 * 1000,
	isBrowserOnly: false,
	isPageShellCritical: true,
});

export const getConsentBannerUserPreferencesResource =
	(): RouteResource<PrefetchedConsentPreferenceData> => consentBannerUserPreferencesResource;

export const useConsentBannerUserPreferencesResource =
	(): UseResourceHookResponse<PrefetchedConsentPreferenceData> =>
		useResource(getConsentBannerUserPreferencesResource());
